import {
  Box,
  type BoxProps,
  Button,
  Center,
  Divider,
  Flex,
  Stack,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { StepCircle } from './StepCircle';
import type { IUseStepState } from './IUseStepState';
import { useStep } from './useStep';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export type StepWizardSteps = Array<
  | {
      title: string;
      description?: string;
      disabled?: boolean;
      page: React.ReactNode;
    }
  | {
      title: string;
      description?: string;
      disabled?: boolean;
      renderPage: (stepState: IUseStepState) => React.ReactNode;
    }
>;

interface StepWizardProps extends BoxProps {
  steps: StepWizardSteps;
  renderStepButtons?: (
    defaultButtons: React.ReactNode,
    stepState: IUseStepState,
  ) => React.ReactNode;
  renderPage?: (defaultPage: React.ReactNode, stepState: IUseStepState) => React.ReactNode;
  onSubmit?: () => void; // Optional function prop
  initialStep?: number;
}
/**
 * this is a more encompassing step component that renders the steps including the step page and the buttons for going back and forth
 */
export function StepWizard(props: StepWizardProps) {
  const { steps, renderPage, renderStepButtons, onSubmit, ...stackProps } = props;
  const isMobile = useBreakpointValue({ base: true, md: false });

  const orientation = useBreakpointValue<'horizontal' | 'vertical'>({
    base: 'vertical',
    md: 'horizontal',
  });

  const stepState = useStep({
    maxStep: steps.length,
    initialStep: props.initialStep ?? 0,
  });

  const { currentStep, setStep } = stepState;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setStep(currentStep - 1);
    }
  };

  const footerButtons = (
    <Flex width={'full'}>
      {currentStep > 0 && (
        <Button variant="ghost" onClick={handleBack} leftIcon={<ChevronLeft />}>
          Back
        </Button>
      )}

      {steps && currentStep < steps.length - 1 && steps[currentStep] ? (
        <Button
          ml="auto"
          variant="solid"
          onClick={handleNext}
          isDisabled={steps[currentStep].disabled}
          rightIcon={<ChevronRight />}
        >
          Next
        </Button>
      ) : null}

      {currentStep === steps.length - 1 && (
        <Button
          ml="auto"
          variant="solid"
          onClick={onSubmit || (() => {})}
          rightIcon={<ChevronRight />}
        >
          Submit
        </Button>
      )}
    </Flex>
  );

  const currentStepDefinition = steps[currentStep];
  const pageContent = currentStepDefinition
    ? 'page' in currentStepDefinition
      ? currentStepDefinition.page
      : currentStepDefinition.renderPage(stepState)
    : null;

  return (
    <>
      <Center>
        <Stack spacing="0" direction={{ base: 'column', md: 'row' }} w="full" py={8}>
          {steps.map((step, index) => {
            const isActive = currentStep === index;
            const isCompleted = currentStep > index;
            const isFirstStep = index === 0;
            const isLastStep = steps.length === index + 1;
            return (
              <Stack
                key={index}
                spacing="4"
                direction={{ base: 'row', md: 'column' }}
                flex="1"
                {...stackProps}
              >
                <Stack spacing="0" align="center" direction={{ base: 'column', md: 'row' }}>
                  <Divider
                    display={isMobile ? 'none' : 'initial'}
                    orientation={orientation}
                    borderWidth="1px"
                    borderColor={
                      isFirstStep ? 'transparent' : isCompleted || isActive ? 'accent' : 'inherit'
                    }
                  />
                  <StepCircle isActive={isActive} isCompleted={isCompleted} />
                  <Divider
                    orientation={orientation}
                    borderWidth="1px"
                    borderColor={isCompleted ? 'accent' : isLastStep ? 'transparent' : 'inherit'}
                  />
                </Stack>
                <Stack
                  spacing="0.5"
                  pb={isMobile && !isLastStep ? '8' : '0'}
                  align={{ base: 'start', md: 'center' }}
                  textAlign="center"
                >
                  <Text color="fg.muted" fontSize="medium">
                    {step.title}
                  </Text>
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </Center>
      <Box pb={10} pt={4}>
        {renderPage ? renderPage(pageContent, stepState) : pageContent}
      </Box>
      {renderStepButtons ? renderStepButtons(footerButtons, stepState) : footerButtons}
    </>
  );
}
